import { useCallback, useEffect, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Link,
  PageBackdrop,
  Rule,
  soundedPointerHandlers,
  StoreHero,
  ui,
  type CapsuleTitle,
} from '../ui';
import { formatDate, formatElapsed, formatMoney } from '@goodbuild/rules/format';
import { formatPlaytime, REFUND_PLAYTIME_HOURS } from '@goodbuild/rules/refund-policy';
import type { EntitlementSource } from '@goodbuild/rules/contracts';
import type { LauncherEntitlement, OwnedTitle } from '@api/views';
import { artUrlFor, useStorePage } from '../catalogue';
import { ApiError, useApi, useApiMutation } from '../api';
import { isLauncher } from '../is-launcher';
import {
  canStart,
  installedTitles,
  launch,
  onGameExited,
  onGameStarted,
  runningGames,
  stopGame,
  type InstallRecord,
} from '../launcher-ipc';
import { Field, Loading, Notice, NotFound, Screen, Table } from './shared';

export function LibraryGameRoute(): React.ReactElement {
  const params = useParams({ strict: false }) as { slug?: string };
  const slug = params.slug ?? '';

  const query = useApi<OwnedTitle>(['library', slug], `/me/library/${encodeURIComponent(slug)}`, {
    enabled: slug !== '',
  });

  useActivationPoll(query.data?.awaitingActivation === true, slug);

  const store = useStorePage(slug);
  const owned = query.data;

  if (query.error?.status === 404) return <NotFound />;

  const art = store.data?.art;
  const productId = owned?.productId ?? '';

  const hero: CapsuleTitle | null = owned
    ? {
        slug: owned.productSlug,
        name: owned.productName,
        studio: store.data?.listing.developerName ?? '',
        price: null,
        artUrl: artUrlFor(art, productId, 'capsule_main'),
        portraitArtUrl: artUrlFor(art, productId, 'library_capsule'),
        heroArtUrl: artUrlFor(art, productId, 'library_hero'),
        logoUrl: artUrlFor(art, productId, 'logo'),
        liveViewers: null,
      }
    : null;

  return (
    <Screen
      title={owned?.productName ?? 'Your game'}
      query={query}
      backdrop={<PageBackdrop imageUrl={artUrlFor(art, productId, 'page_background')} />}
      bleed={<StoreHero title={hero} tall headingLevel="h1" />}
    >
      {owned ? (
        <div className={ui.stack ?? ''}>
          <PlayAction owned={owned} />
          <Facts owned={owned} />
          <Refund owned={owned} />

          <Rule />

          <p className={ui.statLabel ?? ''}>
            <Link href="/library">Back to your library</Link> ·{' '}
            <Link href={`/g/${owned.productSlug}`}>The shop page</Link>
          </p>
        </div>
      ) : null}
    </Screen>
  );
}

const HOW_IT_ARRIVED: Record<EntitlementSource, string> = {
  purchase: 'You bought it.',
  key: 'You redeemed a key for it.',
  gift: 'A gift somebody bought for you.',
  playtest: 'You were let into a playtest.',
  bundle: 'It came in a bundle.',
  first_hundred: 'You were one of the first hundred.',
  admin_gift: 'We gave it to you.',
};

function Facts({ owned }: { owned: OwnedTitle }): React.ReactElement {
  const bought = owned.purchase;

  return (
    <>
      <h2 className={ui.sectionTitle ?? ''}>Your copy</h2>

      <Table head={['', '']}>
        <tr>
          <td>Played</td>
          <td>
            <span className={ui.numeric ?? ''}>{formatPlaytime(owned.playtimeMinutes)}</span>
            <br />
            <span className={ui.statLabel ?? ''}>
              Counted from sessions actually recorded, not from how long the game was open.
            </span>
          </td>
        </tr>

        {bought ? (
          <tr>
            <td>Bought</td>
            <td>
              {formatDate(bought.placedAt)} — {formatElapsed(new Date(bought.placedAt))}
              <br />
              <span className={ui.statLabel ?? ''}>
                {formatMoney(bought.currency, bought.pricePaidMinor)}, tax included
              </span>
            </td>
          </tr>
        ) : (
          <tr>
            <td>How you got it</td>
            <td>
              {HOW_IT_ARRIVED[owned.source]}
              <br />
              <span className={ui.statLabel ?? ''}>
                You paid nothing for it, so there is nothing to send back.
              </span>
            </td>
          </tr>
        )}

        <tr>
          <td>Status</td>
          <td>
            {owned.revokedAt
              ? `No longer yours — ${owned.revokedReason?.replace(/_/g, ' ') ?? 'no reason given'}`
              : owned.expiresAt
                ? `Yours until ${formatDate(owned.expiresAt)}`
                : 'Yours to keep'}
          </td>
        </tr>
      </Table>
    </>
  );
}

function Refund({ owned }: { owned: OwnedTitle }): React.ReactElement | null {
  const call = useApiMutation();
  const cache = useQueryClient();

  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const bought = owned.purchase;
  const decision = owned.refund;
  const asked = owned.refundRequest;

  if (!bought || !decision) return null;

  async function ask(): Promise<void> {
    if (!bought) return;

    setBusy(true);
    setFailure(null);

    try {
      await call(`/me/orders/${bought.orderId}/refund`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() || null }),
      });
      setSent(true);
      setConfirming(false);
      await cache.invalidateQueries({ queryKey: ['library'] });
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  }

  const waiting = sent || asked?.state === 'pending';

  return (
    <>
      <Rule />
      <h2 className={ui.sectionTitle ?? ''}>Your money back</h2>

      {failure ? <Notice alert>{failure}</Notice> : null}

      {waiting ? (
        <>
          <p className="gb-body" role="status">
            You have asked for a refund
            {asked && !sent ? ` on ${formatDate(asked.requestedAt)}` : ''}. A person reads every
            one, and we answer within three working days.
          </p>
          <p className="gb-body">
            Nothing has been taken from your library yet, and you can keep playing while you wait.
            If you do, the playing time still counts.
          </p>
          <p className={ui.statLabel ?? ''}>
            <Link href="/legal/refunds">The refund policy</Link> ·{' '}
            <Link href="/contact">Ask a person</Link>
          </p>
        </>
      ) : asked?.state === 'rejected' ? (
        <>
          <p className="gb-body">
            We looked at this on {formatDate(asked.decidedAt ?? asked.requestedAt)} and did not
            refund it.
          </p>
          {asked.decisionNote ? <p className="gb-body">{asked.decisionNote}</p> : null}
          <p className="gb-body">
            If that looks wrong to you, write to us — a person will read it properly.
          </p>
          <p className={ui.statLabel ?? ''}>
            <Link href="/contact">Write to us</Link> ·{' '}
            <Link href="/legal/refunds">The refund policy</Link>
          </p>
        </>
      ) : asked?.state === 'approved' ? (
        <p className="gb-body">
          This was refunded on {formatDate(asked.decidedAt ?? asked.requestedAt)}. The money can
          take a few days to reach your bank.
        </p>
      ) : decision.eligible ? (
        <>
          <p className="gb-body">
            You have played it for under {REFUND_PLAYTIME_HOURS} hours and bought it recently
            enough, so you can ask for your money back. A person checks every request and answers
            within three working days.
          </p>

          {confirming ? (
            <>
              <Field label="Why, if you want to say (optional)">
                <input
                  className={ui.input ?? ''}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="It would not start on my machine"
                  maxLength={2000}
                />
              </Field>
              <div className={ui.actions ?? ''}>
                <button
                  type="button"
                  className={ui.button ?? ''}
                  disabled={busy}
                  onClick={() => void ask()}
                  {...soundedPointerHandlers}
                >
                  {busy
                    ? 'Sending…'
                    : `Ask for ${formatMoney(bought.currency, bought.pricePaidMinor)} back`}
                </button>
                <button
                  type="button"
                  className={ui.buttonSecondary ?? ''}
                  disabled={busy}
                  onClick={() => setConfirming(false)}
                >
                  Keep it
                </button>
              </div>
            </>
          ) : (
            <div className={ui.actions ?? ''}>
              <button
                type="button"
                className={ui.buttonSecondary ?? ''}
                onClick={() => setConfirming(true)}
                {...soundedPointerHandlers}
              >
                Ask for a refund
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          {decision.refusals.map((refusal) => (
            <p key={`${refusal.code}-${refusal.productSlug ?? ''}`} className="gb-body">
              {refusal.message}
            </p>
          ))}
          <p className={ui.statLabel ?? ''}>
            <Link href="/legal/refunds">The refund policy</Link> ·{' '}
            <Link href="/contact">Ask a person</Link>
          </p>
        </>
      )}
    </>
  );
}

const ACTIVATION_POLL_MS = 3000;

function useActivationPoll(waiting: boolean, slug: string): void {
  const cache = useQueryClient();

  useEffect(() => {
    if (!waiting) return;

    const timer = window.setInterval(() => {
      void cache.invalidateQueries({ queryKey: ['library', slug] });
    }, ACTIVATION_POLL_MS);

    return () => window.clearInterval(timer);
  }, [waiting, cache, slug]);
}

function ActivatingGame({ name }: { name: string }): React.ReactElement {
  return (
    <>
      <h2 className={ui.sectionTitle ?? ''}>Setting up {name}</h2>
      <Loading label="Activating your copy" />
      <p className="gb-body">
        We are activating your copy with our payment provider. It takes a few seconds. This page
        updates itself, so you can wait here.
      </p>
      <p className={ui.statLabel ?? ''}>
        The game is yours &mdash; your receipt is already in your orders. It will be ready to
        install as soon as this finishes. If it is still here in a few minutes,{' '}
        <Link href="/contact">tell us</Link> and we will sort it out.
      </p>
    </>
  );
}

function PlayAction({ owned }: { owned: OwnedTitle }): React.ReactElement {
  if (owned.awaitingActivation) return <ActivatingGame name={owned.productName} />;

  if (!isLauncher()) {
    return (
      <>
        <h2 className={ui.sectionTitle ?? ''}>Play it</h2>
        <p className="gb-body">
          Games are played through the GOODBUILD launcher, which is free and installs this one for
          you. It is already yours — signing in there is all it takes.
        </p>
        <div className={ui.actions ?? ''}>
          <Link href="/download" className={ui.button ?? ''}>
            Install the launcher
          </Link>
        </div>
      </>
    );
  }

  return <LauncherPlay owned={owned} />;
}

interface LauncherLibrary {
  entitlements: LauncherEntitlement[];
}

function LauncherPlay({ owned }: { owned: OwnedTitle }): React.ReactElement {
  const library = useApi<LauncherLibrary>(['launcher', 'library'], '/launcher/library');

  const [installed, setInstalled] = useState<InstallRecord | null>(null);
  const [looked, setLooked] = useState(false);
  const [running, setRunning] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const productId = owned.productId;

  const refreshInstalled = useCallback(() => {
    void installedTitles()
      .then((records) => {
        setInstalled(records.find((record) => record.product_id === productId) ?? null);
        setLooked(true);
      })
      .catch(() => setLooked(true));
  }, [productId]);

  useEffect(refreshInstalled, [refreshInstalled]);

  useEffect(() => {
    void runningGames()
      .then((titles) => setRunning(titles.includes(productId)))
      .catch(() => {});

    const stops = [
      onGameStarted((event) => {
        if (event.product_id === productId) setRunning(true);
      }),
      onGameExited((event) => {
        if (event.product_id !== productId) return;
        setRunning(false);
        refreshInstalled();
      }),
    ];

    return () => {
      for (const stop of stops) stop();
    };
  }, [productId, refreshInstalled]);

  const entitlement =
    library.data?.entitlements.find((held) => held.product_id === productId) ?? null;

  async function play(): Promise<void> {
    if (!entitlement) return;

    setFailure(null);

    try {
      const decision = await canStart(entitlement, null, Math.floor(Date.now() / 1000));
      if (!decision.allowed) {
        setFailure('That will not start just now. Open Downloads and it will say why.');
        return;
      }

      await launch(productId);
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : 'That did not start. Try again.');
    }
  }

  const ready = installed !== null && installed.state.state === 'ready' && installed.launchable;

  return (
    <>
      <h2 className={ui.sectionTitle ?? ''}>Play it</h2>

      {failure ? <Notice alert>{failure}</Notice> : null}

      {!looked ? (
        <Loading label="Looking on this machine" />
      ) : running ? (
        <>
          <p className="gb-body">It is running.</p>
          <div className={ui.actions ?? ''}>
            <button
              type="button"
              className={ui.buttonSecondary ?? ''}
              onClick={() => void stopGame(productId).catch(() => {})}
            >
              Stop it
            </button>
          </div>
        </>
      ) : ready ? (
        <div className={ui.actions ?? ''}>
          <button
            type="button"
            className={ui.button ?? ''}
            disabled={entitlement === null}
            onClick={() => void play()}
            {...soundedPointerHandlers}
          >
            Play
          </button>
        </div>
      ) : installed === null ? (
        <>
          <p className="gb-body">It is not on this machine yet.</p>
          <div className={ui.actions ?? ''}>
            <Link href="/downloads" className={ui.button ?? ''}>
              Install it
            </Link>
          </div>
        </>
      ) : (
        <>
          <p className="gb-body">
            {installed.state.state === 'applying'
              ? 'It is still installing.'
              : 'This build does not say how to start itself. Downloads can repair it.'}
          </p>
          <div className={ui.actions ?? ''}>
            <Link href="/downloads" className={ui.buttonSecondary ?? ''}>
              Open Downloads
            </Link>
          </div>
        </>
      )}
    </>
  );
}
